import React, { useState, useEffect } from 'react';
import { Plus, Menu, User, Search, ChevronRight, X, Info, Heart, LogOut, BarChart3, Home, Tag, Eye } from 'lucide-react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail 
} from 'firebase/auth';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  onSnapshot,
  serverTimestamp,
  query,
  orderBy 
} from 'firebase/firestore';
import { auth, db } from './firebase';

// Demo data for guest mode
const demoData = [
  {
    id: 'demo-1',
    name: 'Sarah Chen',
    interactionHistory: [
      {
        id: 1,
        type: 'meeting',
        sentiment: 'positive',
        date: 'Jul 25, 25',
        time: '2:30 PM',
        notes: 'Great coffee chat about her startup idea - very exciting project!',
        tags: ['work', 'networking'],
        timestamp: Date.now() - 1000 * 60 * 60 * 2 // 2 hours ago
      },
      {
        id: 2,
        type: 'call',
        sentiment: 'positive',
        date: 'Jul 23, 25',
        time: '11:15 AM',
        notes: 'Catch up call, she got the promotion at her company!',
        tags: ['work', 'celebration'],
        timestamp: Date.now() - 1000 * 60 * 60 * 48 // 2 days ago
      }
    ],
    createdAt: new Date()
  },
  {
    id: 'demo-2',
    name: 'Mom',
    interactionHistory: [
      {
        id: 3,
        type: 'call',
        sentiment: 'neutral',
        date: 'Jul 24, 25',
        time: '7:00 PM',
        notes: 'Weekly check-in call, she\'s doing well with her garden',
        tags: ['family'],
        timestamp: Date.now() - 1000 * 60 * 60 * 24 // 1 day ago
      },
      {
        id: 4,
        type: 'text',
        sentiment: 'positive',
        date: 'Jul 22, 25',
        time: '9:45 AM',
        notes: 'Sent beautiful photos from her garden, tomatoes are growing!',
        tags: ['family', 'hobbies'],
        timestamp: Date.now() - 1000 * 60 * 60 * 72 // 3 days ago
      }
    ],
    createdAt: new Date()
  },
  {
    id: 'demo-3',
    name: 'Jake Martinez',
    interactionHistory: [
      {
        id: 5,
        type: 'meeting',
        sentiment: 'negative',
        date: 'Jul 23, 25',
        time: '3:15 PM',
        notes: 'Disagreement on project direction, need to follow up',
        tags: ['work', 'conflict'],
        timestamp: Date.now() - 1000 * 60 * 60 * 36 // 1.5 days ago
      },
      {
        id: 6,
        type: 'email',
        sentiment: 'neutral',
        date: 'Jul 20, 25',
        time: '10:30 AM',
        notes: 'Project status update, on track for deadline',
        tags: ['work'],
        timestamp: Date.now() - 1000 * 60 * 60 * 120 // 5 days ago
      }
    ],
    createdAt: new Date()
  }
];

// Simple Fast Landing Page Component
const LandingPage = ({ onShowLogin, onShowSignup, onStartDemo }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">meet.again</h1>
                <p className="text-gray-600">relationship tracker</p>
              </div>
            </div>
          </div>

          {/* Main Headline */}
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            when did you last
            <br />
            <span className="text-blue-600">remember to remember?</span>
          </h2>

          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Track meaningful interactions and strengthen relationships. 
            Never forget the moments that matter most.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button 
              onClick={onShowSignup}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-colors"
            >
              Get Started Free
            </button>
            <button 
              onClick={onShowLogin}
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-xl font-semibold text-lg transition-colors"
            >
              Sign In
            </button>
            <button 
              onClick={onStartDemo}
              className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-4 rounded-xl font-semibold text-lg transition-colors flex items-center gap-2 justify-center"
            >
              <Eye className="w-5 h-5" />
              Try It Yourself
            </button>
          </div>

          {/* Simple Features */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💭</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Remember Everything</h3>
              <p className="text-gray-600">Capture important conversations and moments with the people you care about.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📈</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Track Relationships</h3>
              <p className="text-gray-600">See patterns in your interactions and understand your relationship dynamics.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔍</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Find Anything</h3>
              <p className="text-gray-600">Search through your interaction history to find specific details and conversations.</p>
            </div>
          </div>

          {/* Simple Demo Preview */}
          <div className="mt-16 bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto">
            <h4 className="font-semibold text-gray-900 mb-4">Recent Interactions</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  S
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Sarah</div>
                  <div className="text-sm text-gray-600">Great coffee chat about her startup</div>
                </div>
                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  M
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Mom</div>
                  <div className="text-sm text-gray-600">Weekly check-in call</div>
                </div>
                <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-sm text-gray-400 border-t border-gray-200">
        © 2025 Tiffany. All rights reserved.
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ people }) => {
  const [timeFilter, setTimeFilter] = useState('week'); // week, month, year, all
  const [selectedTag, setSelectedTag] = useState('all');

  // Get all interactions from all people
  const getAllInteractions = () => {
    const allInteractions = [];
    people.forEach(person => {
      if (person.interactionHistory) {
        person.interactionHistory.forEach(interaction => {
          allInteractions.push({
            ...interaction,
            personName: person.name,
            personId: person.id
          });
        });
      }
    });
    return allInteractions.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  };

  // Filter interactions by time
  const getFilteredInteractions = () => {
    const allInteractions = getAllInteractions();
    const now = new Date();
    
    let cutoffDate;
    switch (timeFilter) {
      case 'week':
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      default:
        return allInteractions;
    }

    return allInteractions.filter(interaction => {
      const interactionDate = new Date(interaction.timestamp || 0);
      return interactionDate >= cutoffDate;
    });
  };

  // Get sentiment stats
  const getSentimentStats = () => {
    const interactions = getFilteredInteractions();
    const filteredByTag = selectedTag === 'all' ? interactions : 
      interactions.filter(i => i.tags && i.tags.includes(selectedTag));

    const stats = { positive: 0, neutral: 0, negative: 0, total: filteredByTag.length };
    filteredByTag.forEach(interaction => {
      stats[interaction.sentiment]++;
    });
    
    return stats;
  };

  // Get all unique tags
  const getAllTags = () => {
    const tags = new Set();
    getAllInteractions().forEach(interaction => {
      if (interaction.tags) {
        interaction.tags.forEach(tag => tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  };

  // Get interactions by tag
  const getInteractionsByTag = () => {
    const interactions = getFilteredInteractions();
    const tagStats = {};
    
    interactions.forEach(interaction => {
      if (interaction.tags && interaction.tags.length > 0) {
        interaction.tags.forEach(tag => {
          if (!tagStats[tag]) tagStats[tag] = { positive: 0, neutral: 0, negative: 0, total: 0 };
          tagStats[tag][interaction.sentiment]++;
          tagStats[tag].total++;
        });
      } else {
        if (!tagStats['untagged']) tagStats['untagged'] = { positive: 0, neutral: 0, negative: 0, total: 0 };
        tagStats['untagged'][interaction.sentiment]++;
        tagStats['untagged'].total++;
      }
    });

    return tagStats;
  };

  const sentimentStats = getSentimentStats();
  const tagStats = getInteractionsByTag();
  const allTags = getAllTags();

  return (
    <div className="space-y-6">
      {/* Time Filter */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: 'week', label: 'This Week' },
          { value: 'month', label: 'This Month' },
          { value: 'year', label: 'This Year' },
          { value: 'all', label: 'All Time' }
        ].map(filter => (
          <button
            key={filter.value}
            onClick={() => setTimeFilter(filter.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              timeFilter === filter.value
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{sentimentStats.total}</div>
          <div className="text-sm text-gray-600">Total Interactions</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="text-2xl font-bold text-emerald-600">{sentimentStats.positive}</div>
          <div className="text-sm text-gray-600">Positive</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="text-2xl font-bold text-amber-600">{sentimentStats.neutral}</div>
          <div className="text-sm text-gray-600">Neutral</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200">
          <div className="text-2xl font-bold text-red-600">{sentimentStats.negative}</div>
          <div className="text-sm text-gray-600">Negative</div>
        </div>
      </div>

      {/* Sentiment Breakdown Chart */}
      {sentimentStats.total > 0 && (
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sentiment Breakdown</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-emerald-500 rounded"></div>
              <div className="flex-1 bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(sentimentStats.positive / sentimentStats.total) * 100}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600 w-12 text-right">
                {Math.round((sentimentStats.positive / sentimentStats.total) * 100)}%
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-amber-500 rounded"></div>
              <div className="flex-1 bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-amber-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(sentimentStats.neutral / sentimentStats.total) * 100}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600 w-12 text-right">
                {Math.round((sentimentStats.neutral / sentimentStats.total) * 100)}%
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <div className="flex-1 bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-red-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(sentimentStats.negative / sentimentStats.total) * 100}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600 w-12 text-right">
                {Math.round((sentimentStats.negative / sentimentStats.total) * 100)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tag Filter */}
      {allTags.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Filter by Tag</h3>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedTag('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedTag === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedTag === tag
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Interactions by Tag */}
      {Object.keys(tagStats).length > 0 && (
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Interactions by Tag</h3>
          <div className="space-y-4">
            {Object.entries(tagStats)
              .sort((a, b) => b[1].total - a[1].total)
              .map(([tag, stats]) => (
              <div key={tag} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 capitalize">
                    {tag === 'untagged' ? 'Untagged' : tag}
                  </span>
                  <span className="text-sm text-gray-600">{stats.total} interactions</span>
                </div>
                <div className="grid grid-cols-3 gap-1 h-2">
                  <div 
                    className="bg-emerald-500 rounded-l"
                    style={{ 
                      width: stats.total > 0 ? `${(stats.positive / stats.total) * 100}%` : '0%',
                      minWidth: stats.positive > 0 ? '4px' : '0px'
                    }}
                  ></div>
                  <div 
                    className="bg-amber-500"
                    style={{ 
                      width: stats.total > 0 ? `${(stats.neutral / stats.total) * 100}%` : '0%',
                      minWidth: stats.neutral > 0 ? '4px' : '0px'
                    }}
                  ></div>
                  <div 
                    className="bg-red-500 rounded-r"
                    style={{ 
                      width: stats.total > 0 ? `${(stats.negative / stats.total) * 100}%` : '0%',
                      minWidth: stats.negative > 0 ? '4px' : '0px'
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{stats.positive} positive</span>
                  <span>{stats.neutral} neutral</span>
                  <span>{stats.negative} negative</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sentimentStats.total === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No interactions yet</h3>
          <p className="text-gray-500">Start logging interactions to see your dashboard analytics</p>
        </div>
      )}
    </div>
  );
};

// Simple Login Component
const LoginForm = ({ onLogin, onSignup, onPasswordReset, onBackToLanding }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        await onSignup(email, password);
      } else {
        await onLogin(email, password);
      }
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await onPasswordReset(email);
      setResetSent(true);
      setShowForgotPassword(false);
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h1>
            <p className="text-gray-600">Enter your email to receive a reset link</p>
          </div>

          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-medium disabled:bg-gray-300"
            >
              {loading ? 'Sending...' : 'Send Reset Email'}
            </button>
          </form>

          <div className="text-center mt-6 space-y-3">
            <button
              onClick={() => setShowForgotPassword(false)}
              className="text-blue-500 hover:text-blue-600 text-sm font-medium"
            >
              Back to Sign In
            </button>
            
            <button
              onClick={onBackToLanding}
              className="text-gray-500 hover:text-gray-600 text-sm block w-full"
            >
              ← Back to Homepage
            </button>
          </div>

          <div className="text-center py-6 text-sm text-gray-400">
            © 2025 Tiffany. All rights reserved.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-gray-600">
            {isSignUp ? 'Start tracking your relationships' : 'Sign in to your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
              required
              minLength="6"
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {resetSent && (
            <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-lg">
              Password reset email sent! Check your inbox.
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-medium disabled:bg-gray-300"
          >
            {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <div className="text-center mt-6 space-y-3">
          {!isSignUp && (
            <button
              onClick={() => setShowForgotPassword(true)}
              className="text-blue-500 hover:text-blue-600 text-sm font-medium block w-full"
            >
              Forgot your password?
            </button>
          )}
          
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-blue-500 hover:text-blue-600 text-sm font-medium"
          >
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </button>
          
          <button
            onClick={onBackToLanding}
            className="text-gray-500 hover:text-gray-600 text-sm block w-full"
          >
            ← Back to Homepage
          </button>
        </div>

        <div className="text-center py-6 text-sm text-gray-400">
          © 2025 Tiffany. All rights reserved.
        </div>
      </div>
    </div>
  );
};

const InteractionTracker = () => {
  // ALL useState hooks MUST come first
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [currentView, setCurrentView] = useState('people'); // 'people' or 'dashboard'
  const [people, setPeople] = useState([]);
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [showLogModal, setShowLogModal] = useState(false);
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [showPersonDetail, setShowPersonDetail] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [newPerson, setNewPerson] = useState({ name: '' });
  const [newInteraction, setNewInteraction] = useState({
    type: 'meeting',
    sentiment: 'neutral',
    notes: '',
    tags: []
  });
  const [showNewPersonInput, setShowNewPersonInput] = useState(false);
  const [newPersonName, setNewPersonName] = useState('');
  const [dataLoading, setDataLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Auth effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Real-time data loading effect
  useEffect(() => {
    if (isGuestMode) {
      setPeople([...demoData]);
      return;
    }

    if (!user) {
      setPeople([]);
      return;
    }

    setDataLoading(true);
    
    // Create a real-time listener for user's people
    const userPeopleRef = collection(db, 'users', user.uid, 'people');
    const q = query(userPeopleRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const peopleData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPeople(peopleData);
      setDataLoading(false);
    }, (error) => {
      console.error('Error loading people:', error);
      setDataLoading(false);
    });

    return unsubscribe;
  }, [user, isGuestMode]);

  // Filter effect
  useEffect(() => {
    let filtered = people;
    
    if (searchTerm) {
      filtered = filtered.filter(person => 
        person.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (sentimentFilter !== 'all') {
      filtered = filtered.filter(person => 
        person.interactionHistory && person.interactionHistory.some(interaction => interaction.sentiment === sentimentFilter)
      );
    }
    
    setFilteredPeople(filtered);
  }, [people, searchTerm, sentimentFilter]);

  // Helper functions
  const getBadgeColor = (type) => {
    switch (type) {
      case 'positive': return 'bg-emerald-500 text-white';
      case 'negative': return 'bg-red-500 text-white';
      case 'neutral': return 'bg-amber-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'meeting': return '🤝';
      case 'call': return '📞';
      case 'email': return '📧';
      case 'text': return '💬';
      case 'social': return '🎉';
      default: return '💼';
    }
  };

  const getSentimentCounts = (interactionHistory) => {
    const counts = { positive: 0, negative: 0, neutral: 0 };
    if (interactionHistory) {
      interactionHistory.forEach(interaction => {
        counts[interaction.sentiment]++;
      });
    }
    return counts;
  };

  const getTotalInteractions = (interactionHistory) => {
    return interactionHistory ? interactionHistory.length : 0;
  };

  const getLastInteractionDate = (interactionHistory) => {
    if (!interactionHistory || interactionHistory.length === 0) return '';
    const sorted = interactionHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0]?.date || '';
  };

  // Tag handling functions
  const addTag = () => {
    if (tagInput.trim() && !newInteraction.tags.includes(tagInput.trim().toLowerCase())) {
      setNewInteraction({
        ...newInteraction,
        tags: [...newInteraction.tags, tagInput.trim().toLowerCase()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setNewInteraction({
      ...newInteraction,
      tags: newInteraction.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Auth functions
  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    return signOut(auth);
  };

  const resetPassword = async (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  const startDemoMode = () => {
    setIsGuestMode(true);
    setShowLogin(false);
  };

  const exitDemoMode = () => {
    setIsGuestMode(false);
    setPeople([]);
    setCurrentView('people');
  };

  // Database functions (modified for guest mode)
  const addNewPerson = async () => {
    if (!newPerson.name.trim()) return;
    
    if (isGuestMode) {
      // In guest mode, just add to local state
      const newPersonData = {
        id: `demo-${Date.now()}`,
        name: newPerson.name.trim(),
        interactionHistory: [],
        createdAt: new Date()
      };
      setPeople([newPersonData, ...people]);
      setNewPerson({ name: '' });
      setShowAddPersonModal(false);
      return;
    }

    if (!user) return;
    
    try {
      const userPeopleRef = collection(db, 'users', user.uid, 'people');
      await addDoc(userPeopleRef, {
        name: newPerson.name.trim(),
        interactionHistory: [],
        createdAt: serverTimestamp()
      });
      
      setNewPerson({ name: '' });
      setShowAddPersonModal(false);
    } catch (error) {
      console.error('Error adding person:', error);
      alert('Error adding person. Please try again.');
    }
  };

  const logInteraction = async () => {
    let targetPersonId = selectedPerson?.id;
    
    // If creating a new person, add them first
    if (showNewPersonInput && newPersonName.trim()) {
      if (isGuestMode) {
        const newPersonData = {
          id: `demo-${Date.now()}`,
          name: newPersonName.trim(),
          interactionHistory: [],
          createdAt: new Date()
        };
        setPeople([newPersonData, ...people]);
        targetPersonId = newPersonData.id;
      } else {
        if (!user) return;
        try {
          const userPeopleRef = collection(db, 'users', user.uid, 'people');
          const newPersonDoc = await addDoc(userPeopleRef, {
            name: newPersonName.trim(),
            interactionHistory: [],
            createdAt: serverTimestamp()
          });
          targetPersonId = newPersonDoc.id;
        } catch (error) {
          console.error('Error adding new person:', error);
          alert('Error adding new person. Please try again.');
          return;
        }
      }
    }
    
    if (!targetPersonId) return;
    
    const newInteractionRecord = {
      id: Date.now(),
      type: newInteraction.type,
      sentiment: newInteraction.sentiment,
      date: new Date().toLocaleDateString('en-US', { 
        month: 'short', 
        day: '2-digit', 
        year: '2-digit' 
      }),
      time: new Date().toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }),
      notes: newInteraction.notes,
      tags: newInteraction.tags || [],
      timestamp: Date.now()
    };

    if (isGuestMode) {
      // In guest mode, update local state
      setPeople(prevPeople => 
        prevPeople.map(person => 
          person.id === targetPersonId
            ? {
                ...person,
                interactionHistory: [newInteractionRecord, ...(person.interactionHistory || [])]
              }
            : person
        )
      );
    } else {
      if (!user) return;
      
      try {
        // Find the person in our local state to get current interactions
        const targetPerson = people.find(p => p.id === targetPersonId);
        const currentInteractions = targetPerson?.interactionHistory || [];
        
        // Update the person document with new interaction
        const personRef = doc(db, 'users', user.uid, 'people', targetPersonId);
        await updateDoc(personRef, {
          interactionHistory: [newInteractionRecord, ...currentInteractions],
          lastUpdated: serverTimestamp()
        });
      } catch (error) {
        console.error('Error logging interaction:', error);
        alert('Error logging interaction. Please try again.');
        return;
      }
    }
    
    // Reset form
    setShowLogModal(false);
    setNewInteraction({ type: 'meeting', sentiment: 'neutral', notes: '', tags: [] });
    setShowNewPersonInput(false);
    setNewPersonName('');
    setTagInput('');
    
    // Don't reset selectedPerson if detail view is open
    if (!showPersonDetail) {
      setSelectedPerson(null);
    }
  };

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading meet.again...</p>
        </div>
      </div>
    );
  }

  // Show landing page for non-authenticated users
  if (!user && !showLogin && !isGuestMode) {
    return (
      <LandingPage 
        onShowLogin={() => setShowLogin(true)}
        onShowSignup={() => setShowLogin(true)}
        onStartDemo={startDemoMode}
      />
    );
  }

  // Show login form
  if (!user && showLogin) {
    return (
      <LoginForm 
        onLogin={login} 
        onSignup={signup} 
        onPasswordReset={resetPassword}
        onBackToLanding={() => {
          setShowLogin(false);
          setIsGuestMode(false);
        }}
      />
    );
  }

  // Main render (for both authenticated users and guest mode)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Demo Mode Banner */}
      {isGuestMode && (
        <div className="bg-amber-100 border-b border-amber-200 px-4 py-3">
          <div className="flex items-center justify-between max-w-md mx-auto">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Demo Mode - Try the app!</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setIsGuestMode(false);
                  setShowLogin(true);
                }}
                className="text-sm bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Sign Up to Save
              </button>
              <button
                onClick={exitDemoMode}
                className="text-sm text-amber-700 hover:text-amber-900"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => setShowMenu(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <div className="text-center">
            <h1 className="text-xl font-bold text-gray-900">meet.again</h1>
            <p className="text-xs text-gray-500">relationship tracker</p>
          </div>
          <div className="w-9"></div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="px-4 pb-4">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setCurrentView('people')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                currentView === 'people'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Home className="w-4 h-4" />
              People
            </button>
            <button
              onClick={() => setCurrentView('dashboard')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                currentView === 'dashboard'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6">
        <div className="max-w-md mx-auto">
          {currentView === 'dashboard' ? (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Dashboard</h2>
                <p className="text-gray-600">Insights into your relationships and interactions</p>
              </div>
              <Dashboard people={people} />
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Your People</h2>
                <p className="text-gray-600">Track meaningful interactions and strengthen relationships</p>
                {dataLoading && (
                  <p className="text-sm text-blue-500 mt-2">Loading your data...</p>
                )}
              </div>
              
              {/* Search and Filters */}
              <div className="mb-6 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search people..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                  />
                </div>
                
                <select
                  value={sentimentFilter}
                  onChange={(e) => setSentimentFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                >
                  <option value="all">All Interactions</option>
                  <option value="positive">Positive Only</option>
                  <option value="neutral">Neutral Only</option>
                  <option value="negative">Negative Only</option>
                </select>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 mb-6">
                <button 
                  onClick={() => {
                    setSelectedPerson(null);
                    setShowLogModal(true);
                  }}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl flex items-center justify-center gap-2 font-medium shadow-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Log Interaction
                </button>
                
                <button 
                  onClick={() => setShowAddPersonModal(true)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-xl flex items-center justify-center transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* People List */}
              <div className="space-y-3">
                {filteredPeople.map((person) => {
                  const sentimentCounts = getSentimentCounts(person.interactionHistory);
                  return (
                    <div 
                      key={person.id} 
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => {
                        setSelectedPerson(person);
                        setShowPersonDetail(true);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                            <User className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{person.name}</div>
                            <div className="text-sm text-gray-500">
                              {getTotalInteractions(person.interactionHistory)} interaction{getTotalInteractions(person.interactionHistory) !== 1 ? 's' : ''}
                              {getLastInteractionDate(person.interactionHistory) && (
                                <> • Last: {getLastInteractionDate(person.interactionHistory)}</>
                              )}
                            </div>
                            <div className="flex gap-1 mt-2">
                              {sentimentCounts.positive > 0 && (
                                <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${getBadgeColor('positive')}`}>
                                  {sentimentCounts.positive}
                                </span>
                              )}
                              {sentimentCounts.neutral > 0 && (
                                <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${getBadgeColor('neutral')}`}>
                                  {sentimentCounts.neutral}
                                </span>
                              )}
                              {sentimentCounts.negative > 0 && (
                                <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${getBadgeColor('negative')}`}>
                                  {sentimentCounts.negative}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300" />
                      </div>
                    </div>
                  );
                })}
                
                {!dataLoading && filteredPeople.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <User className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {people.length === 0 ? "Start your relationship journey" : "No people found"}
                    </h3>
                    <p className="text-gray-500 mb-4">
                      {people.length === 0 
                        ? "Add someone important to you and start tracking meaningful interactions" 
                        : "Try adjusting your search or filters"
                      }
                    </p>
                    {people.length === 0 && (
                      <button
                        onClick={() => setShowAddPersonModal(true)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
                      >
                        Add Your First Person
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Copyright Footer */}
      <div className="text-center py-6 text-sm text-gray-400">
        © 2025 Tiffany. All rights reserved.
      </div>

      {/* Side Menu */}
      {showMenu && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowMenu(false)}></div>
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">meet.again</h3>
                  <p className="text-sm text-gray-500">relationship tracker</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {isGuestMode ? 'Demo Mode' : `Signed in as ${user?.email}`}
                  </p>
                </div>
                <button 
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-2">
              {isGuestMode && (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setIsGuestMode(false);
                    setShowLogin(true);
                  }}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left bg-blue-50 text-blue-600"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Sign Up to Save Data</span>
                </button>
              )}
              
              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowAbout(true);
                }}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
              >
                <Info className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-gray-700">About meet.again</span>
              </button>
              
              {isGuestMode ? (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    exitDemoMode();
                  }}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left text-red-600"
                >
                  <X className="w-5 h-5" />
                  <span className="font-medium">Exit Demo</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left text-red-600"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* About Modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">About meet.again</h2>
                <button
                  onClick={() => setShowAbout(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">when did you last remember to remember?</h3>
              </div>
              
              <div className="space-y-4 text-gray-600">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Who are we but the sum of our memories?</h4>
                  <p className="mb-4">
                    You know that feeling when someone mentions something important, but by the time you see them again, you've completely forgotten? Or when your friend shares a beautiful quote that you want to remember for life?
                  </p>
                  <p className="mb-4">
                    We have these amazing interactions every day - some stick with us, but honestly, most of them just... slip away. And that's not our fault. Our brains aren't built to remember every detail from each chat or interaction, especially across dozens of relationships.
                  </p>
                  <p className="mb-6">
                    These interactions are what forms memories. And I strongly believe that we are the sum of our memories. That's exactly why I built meet.again.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">How you can use meet.again</h4>
                  <p className="mb-3">meet.again works as your relationship memory, helping you:</p>
                  <ul className="space-y-2 text-sm ml-4">
                    <li>• Save those "I should remember this" moments right after conversations</li>
                    <li>• Track how interactions make you feel over time</li>
                    <li>• Build a searchable history with everyone important in your life</li>
                    <li>• Find specific details months later when you need them</li>
                    <li>• Notice patterns you'd miss without seeing the full picture</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Person Detail Modal */}
      {showPersonDetail && selectedPerson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedPerson.name}</h2>
                    <p className="text-gray-600">
                      {getTotalInteractions(selectedPerson.interactionHistory)} total interactions
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPersonDetail(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="mt-4">
                <button
                  onClick={() => {
                    setShowPersonDetail(false);
                    setShowLogModal(true);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Log New Interaction
                </button>
              </div>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <h3 className="font-semibold text-lg mb-4">Interaction History</h3>
              
              {getTotalInteractions(selectedPerson.interactionHistory) === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No interactions yet</h4>
                  <p className="text-gray-500 mb-4">Start tracking your relationship with {selectedPerson.name}</p>
                  <button
                    onClick={() => {
                      setShowPersonDetail(false);
                      setShowLogModal(true);
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
                  >
                    Log First Interaction
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedPerson.interactionHistory
                    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
                    .map((interaction) => (
                    <div 
                      key={interaction.id} 
                      className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        <div className="text-2xl">{getInteractionIcon(interaction.type)}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-medium capitalize text-gray-900">{interaction.type}</span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getBadgeColor(interaction.sentiment)}`}>
                              {interaction.sentiment}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 mb-3">
                            {interaction.date} at {interaction.time}
                          </div>
                          {interaction.tags && interaction.tags.length > 0 && (
                            <div className="flex gap-1 mb-3">
                              {interaction.tags.map((tag, index) => (
                                <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {interaction.notes && (
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-sm text-gray-700">{interaction.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Person Modal */}
      {showAddPersonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-6 text-center">Add New Person</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newPerson.name}
                    onChange={(e) => setNewPerson({...newPerson, name: e.target.value})}
                    placeholder="Enter person's name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => {
                    setShowAddPersonModal(false);
                    setNewPerson({ name: '' });
                  }}
                  className="flex-1 px-6 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={addNewPerson}
                  disabled={!newPerson.name.trim()}
                  className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  Add Person
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Interaction Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Log New Interaction</h2>
                <button
                  onClick={() => {
                    setShowLogModal(false);
                    setSelectedPerson(null);
                    setNewInteraction({ type: 'meeting', sentiment: 'neutral', notes: '', tags: [] });
                    setShowNewPersonInput(false);
                    setNewPersonName('');
                    setTagInput('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Person
                  </label>
                  <select
                    value={showNewPersonInput ? 'new' : (selectedPerson?.id || '')}
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        setShowNewPersonInput(true);
                        setSelectedPerson(null);
                      } else {
                        setShowNewPersonInput(false);
                        setNewPersonName('');
                        const person = people.find(p => p.id === e.target.value);
                        setSelectedPerson(person);
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a person</option>
                    {people.map(person => (
                      <option key={person.id} value={person.id}>{person.name}</option>
                    ))}
                    <option value="new">+ Add New Person</option>
                  </select>
                </div>

                {showNewPersonInput && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Person Name
                    </label>
                    <input
                      type="text"
                      value={newPersonName}
                      onChange={(e) => setNewPersonName(e.target.value)}
                      placeholder="Enter person's name"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interaction Type
                  </label>
                  <select
                    value={newInteraction.type}
                    onChange={(e) => setNewInteraction({...newInteraction, type: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="meeting">🤝 Meeting</option>
                    <option value="call">📞 Phone Call</option>
                    <option value="email">📧 Email</option>
                    <option value="text">💬 Text Message</option>
                    <option value="social">🎉 Social Media</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How did it go?
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'positive', label: 'Positive', color: 'bg-emerald-500', emoji: '😊' },
                      { value: 'neutral', label: 'Neutral', color: 'bg-amber-500', emoji: '😐' },
                      { value: 'negative', label: 'Negative', color: 'bg-red-500', emoji: '😔' }
                    ].map((sentiment) => (
                      <button
                        key={sentiment.value}
                        onClick={() => setNewInteraction({...newInteraction, sentiment: sentiment.value})}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          newInteraction.sentiment === sentiment.value
                            ? `${sentiment.color} text-white border-transparent`
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-lg mb-1">{sentiment.emoji}</div>
                        <div className="text-xs font-medium">{sentiment.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (Optional)
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagKeyPress}
                      placeholder="Add tag (e.g., work, exercise)"
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <button
                      onClick={addTag}
                      className="px-4 py-2 bg-blue-100 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                    >
                      <Tag className="w-4 h-4" />
                    </button>
                  </div>
                  {newInteraction.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {newInteraction.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center gap-1 cursor-pointer hover:bg-blue-200"
                          onClick={() => removeTag(tag)}
                        >
                          {tag}
                          <X className="w-3 h-3" />
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={newInteraction.notes}
                    onChange={(e) => setNewInteraction({...newInteraction, notes: e.target.value})}
                    placeholder="Add notes about this interaction..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => {
                    setShowLogModal(false);
                    setSelectedPerson(null);
                    setNewInteraction({ type: 'meeting', sentiment: 'neutral', notes: '', tags: [] });
                    setShowNewPersonInput(false);
                    setNewPersonName('');
                    setTagInput('');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={logInteraction}
                  disabled={!selectedPerson && !(showNewPersonInput && newPersonName.trim())}
                  className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  Log Interaction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractionTracker;